import { Children, Fragment, cloneElement, isValidElement } from 'react';
import type { CSSProperties, MouseEvent, ReactElement, ReactNode } from 'react';
import { filler } from '../content/copy';

export function Frame({
  children,
  size = 'wide',
  className = '',
}: {
  children: ReactNode;
  size?: 'wide' | 'medium' | 'small';
  className?: string;
}) {
  return <div className={`frame frame--${size} ${className}`}>{children}</div>;
}
export function Accent({ children }: { children: ReactNode }) {
  return <span className="accent">{children}</span>;
}
function words(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === 'string')
      return child.split(/(\s+)/).map((word, index) =>
        /\s/.test(word) ? (
          word
        ) : (
          <span className="reveal-word" key={index}>
            {word}
          </span>
        ),
      );
    if (isValidElement<{ children?: ReactNode }>(child) && child.props.children) {
      if (child.type === Accent)
        return <span className="accent reveal-accent">{child.props.children}</span>;
      return cloneElement(child, {}, words(child.props.children));
    }
    return child;
  });
}
export function Heading({
  children,
  level = 2,
  size = 'display',
  className = '',
  always = false,
  reveal = true,
}: {
  children: ReactNode;
  level?: 1 | 2 | 3;
  size?: 'display' | 'large' | 'medium' | 'small';
  className?: string;
  always?: boolean;
  reveal?: boolean;
}) {
  const Tag = `h${level}` as 'h1' | 'h2' | 'h3';
  return (
    <Tag
      className={`heading heading--${size} ${className}`}
      data-reveal={reveal ? (always ? 'all' : 'desktop') : undefined}
    >
      {reveal ? words(children) : children}
    </Tag>
  );
}
export function Mark({ className = '' }: { className?: string }) {
  return <span className={`wordmark ${className}`}>Lorem</span>;
}
export function Icon({
  name,
  className = '',
}: {
  name:
    | 'instagram'
    | 'youtube'
    | 'tiktok'
    | 'plus'
    | 'equals'
    | 'play'
    | 'question'
    | 'close'
    | 'menu'
    | 'trophy';
  className?: string;
}) {
  const shapes: Record<typeof name, ReactElement> = {
    instagram: (
      <>
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="17.5" cy="6.5" r="1.2" />
      </>
    ),
    youtube: (
      <>
        <path d="M22 7c-.3-1.5-1.2-2.4-2.7-2.6C17 4 7 4 4.7 4.4 3.2 4.6 2.3 5.5 2 7c-.5 2-.5 8 0 10 .3 1.5 1.2 2.4 2.7 2.6 2.3.4 12.3.4 14.6 0 1.5-.2 2.4-1.1 2.7-2.6.5-2 .5-8 0-10Z" />
        <path d="m10 8 6 4-6 4Z" fill="var(--icon-cutout, var(--paper))" />
      </>
    ),
    tiktok: (
      <path d="M16.6 2h-3.4v13.4a2.9 2.9 0 1 1-2.1-2.8V9.1a6.4 6.4 0 1 0 5.5 6.3V8.6a8 8 0 0 0 4.4 1.4V6.6a4.5 4.5 0 0 1-4.4-4.6Z" />
    ),
    plus: <path d="M10 1h4v9h9v4h-9v9h-4v-9H1v-4h9Z" />,
    equals: <path d="M1 5h22v5H1zm0 10h22v5H1z" />,
    play: <path d="m7 3 15 9-15 9Z" />,
    question: (
      <path d="M12 1.5c-3.9 0-6.5 2.4-6.5 5.9h3.7c0-1.6 1.1-2.7 2.8-2.7 1.6 0 2.7 1 2.7 2.4 0 1.2-.5 1.9-2 2.8-2 1.2-2.8 2.5-2.7 4.7v1h3.6v-.8c0-1.3.4-2 2.2-3.1 1.9-1.2 3-2.7 3-4.9 0-3.3-2.6-5.3-6.8-5.3ZM9.8 20.3a2.2 2.2 0 1 0 4.4 0 2.2 2.2 0 0 0-4.4 0Z" />
    ),
    close: <path d="m5 3 7 7 7-7 2 2-7 7 7 7-2 2-7-7-7 7-2-2 7-7-7-7Z" />,
    menu: <path d="M3 5h18v2H3zm0 6h18v2H3zm0 6h18v2H3z" />,
    trophy: (
      <>
        <path d="M7 2h10v8a5 5 0 0 1-4 5v4h5v3H6v-3h5v-4a5 5 0 0 1-4-5Z" />
        <path
          d="M7 5H3v4c0 3 2 4 5 4M17 5h4v4c0 3-2 4-5 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      </>
    ),
  };
  return (
    <svg className={`icon ${className}`} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {shapes[name]}
    </svg>
  );
}
export function Action({
  children = filler.short,
  href,
  tone = 'blue',
  size = 'full',
  tilt = 0,
  className = '',
  onClick,
  type = 'button',
  disabled,
  external = false,
}: {
  children?: ReactNode;
  href?: string;
  tone?:
    | 'blue'
    | 'outline'
    | 'pink'
    | 'join'
    | 'youtube'
    | 'instagram'
    | 'tiktok'
    | 'light'
    | 'purple'
    | 'green'
    | 'video';
  size?: 'full' | 'compact' | 'nav';
  tilt?: number;
  className?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  /* Opens the link in a new tab. */
  external?: boolean;
}) {
  const props = {
    className: `action action--${tone} action--${size} ${className}`,
    style: { '--tilt': `${tilt}deg` } as CSSProperties,
    onClick,
  };
  return href ? (
    <a {...props} href={href} {...(external && { target: '_blank', rel: 'noopener noreferrer' })}>
      {children}
    </a>
  ) : (
    <button {...props} type={type} disabled={disabled}>
      {children}
    </button>
  );
}
